import { useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { Field, Formik, useField, useFormikContext } from 'formik';
import { UserRole, useMeQuery } from 'legoland-sdk/dist/experimental';
import { useEffect, useMemo } from 'react';
import { Box, Button, Input, Select, TooltipOnHover, useToasts } from 'tombac';
import { ErrorIcon } from 'tombac-icons';
import { mixed, object, string } from 'yup';
import { convertCommunicationFormValuesToItem } from "./CommunicationFormModal/helpers/convertCommunicationFormValuesToItem";
import { useSendCommunicationTestEmailMutation } from "./communicationsApiHooks";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const validationSchema = object({
  testEmail: string().email('The email address is not valid.').required('The email address is required.'),
  role: mixed().oneOf(Object.values(UserRole)).required('The role is required.')
});
export const CommunicationTestEmailSender = _ref => {
  let {
    communicationItem
  } = _ref;
  const addApiErrorToast = useApiErrorToast();
  const {
    addToast
  } = useToasts();
  const communicationForm = useFormikContext();
  if (!communicationItem && !communicationForm) {
    throw new Error('Either communicationItemId must be provided to CommunicationTestEmailSender ' + 'or the component must be used within CommunicationFormModal.');
  }
  const meQuery = useMeQuery();
  const initialValues = useMemo(() => ({
    testEmail: meQuery.data?.me.email
  }), [meQuery.data?.me.email]);
  const {
    mutateAsync: sendCommunicationTestEmail
  } = useSendCommunicationTestEmailMutation();
  const handleSubmit = async _ref2 => {
    let {
      testEmail,
      role
    } = _ref2;
    try {
      await sendCommunicationTestEmail({
        to: testEmail,
        role,
        ...(communicationItem ? {
          communicationItemId: communicationItem.id
        } : {
          communicationItem: convertCommunicationFormValuesToItem(communicationForm.values)
        })
      });
      addToast('Test email sent successfully', 'success', {
        content: `The test email was sent to ${testEmail}.`
      });
    } catch (error) {
      addApiErrorToast(error);
    }
  };
  return /*#__PURE__*/_jsx(Formik, {
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
    "data-sentry-element": "Formik",
    "data-sentry-component": "CommunicationTestEmailSender",
    "data-sentry-source-file": "CommunicationTestEmailSender.tsx",
    children: _ref3 => {
      let {
        errors,
        isSubmitting,
        isValid,
        touched,
        submitForm
      } = _ref3;
      return /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        children: [/*#__PURE__*/_jsx(Field, {
          $width: "235u",
          append: touched.testEmail && errors.testEmail ? /*#__PURE__*/_jsx(TooltipOnHover, {
            content: errors.testEmail,
            size: "s",
            variant: "danger",
            children: /*#__PURE__*/_jsx(ErrorIcon, {
              fixedWidth: false
            })
          }) : null,
          as: Input,
          invalid: Boolean(touched.testEmail && errors.testEmail),
          name: "testEmail",
          type: "email"
        }), /*#__PURE__*/_jsx(RoleSelectField, {
          $ml: "1sp",
          availableRoles: communicationItem ? communicationItem.roles : communicationForm.values.roles,
          name: "role"
        }), /*#__PURE__*/_jsx(Button, {
          $ml: "1sp",
          busy: isSubmitting,
          disabled: !isValid || communicationForm && !communicationForm.isValid,
          onClick: submitForm,
          type: "button",
          children: "Send test email"
        })]
      });
    }
  });
};
const RoleSelectField = _ref4 => {
  let {
    availableRoles,
    name,
    ...stylingProps
  } = _ref4;
  const [{
    value
  },, {
    setValue
  }] = useField(name);
  const roleOptions = useMemo(() => availableRoles.map(value => ({
    label: Object.entries(UserRole).find(_ref5 => {
      let [, role] = _ref5;
      return role === value;
    })?.[0],
    value
  })).sort((a, b) => a.label.localeCompare(b.label)), [availableRoles]);
  useEffect(() => {
    if (!roleOptions.some(option => option.value === value)) {
      setValue(roleOptions?.[0]?.value);
    }
  }, [roleOptions, setValue, value]);
  return /*#__PURE__*/_jsx(Select, {
    ...stylingProps,
    menuPlacement: "top",
    onChange: option => setValue(option.value),
    options: roleOptions,
    value: roleOptions.find(option => option.value === value),
    "data-sentry-element": "Select",
    "data-sentry-component": "RoleSelectField",
    "data-sentry-source-file": "CommunicationTestEmailSender.tsx"
  });
};